import { GB_PER_TB } from '../../../core/services/quick-sizer-response.mapper';
import {
  LimitingResource,
  QuickSizerOutput,
  QuickSizerVmSizingRow,
  WorkloadPool,
} from '../models/quick-sizer.models';
import { availableForWorkloadsGb } from './capacity-waterfall.rows';

/** The three cluster pools the engine's max-VM counts divide. */
export interface ClusterPools {
  availableVcpus: number;
  availableMemoryGb: number;
  availableStorageGb: number;
  /** Share of one VM's vCPU, RAM and disk the engine charges (average use and growth). */
  cpuDemandFactor: number;
  memoryDemandFactor: number;
  storageDemandFactor: number;
}

/** How much of one cluster pool the max VM count uses. */
export interface HeadroomRow {
  resource: 'CPU' | 'Memory' | 'Storage';
  used: number;
  pool: number;
  percent: number;
  /** `percent` clamped to 0-100 for the bar. */
  barPercent: number;
  unit: string;
  /** DecimalPipe digits for `used` and `pool`. */
  digits: string;
  /** The pool named in `Limited by`. */
  limiting: boolean;
}

export const VM_LIMIT_PER_HOST_TOOLTIP =
  'vSAN allows at most 200 VMs per host, VCF component VMs included. More hosts raise this count; '
  + 'bigger hosts do not.';

const LIMITER_NOTE: Record<LimitingResource, string> = {
  CPU: 'vCPU is what runs out first. More cores per host, or a higher vCPU / pCore ratio, '
    + 'raises this count.',
  Memory: 'RAM is what runs out first. More memory per host, or a size with less RAM per VM, '
    + 'raises this count.',
  Storage: 'Storage is what runs out first. Larger drives, or more drives per host, raises this '
    + 'count.',
  EXCEEDS_SINGLE_HOST: 'A virtual machine cannot span hosts, so no amount of cluster capacity '
    + 'places this size.',
  VM_DENSITY: VM_LIMIT_PER_HOST_TOOLTIP,
};

/** What the `Limited by` chip means. */
export function limiterNote(row: QuickSizerVmSizingRow): string {
  return LIMITER_NOTE[row.limitingResource] ?? LIMITER_NOTE.CPU;
}

/** Reads the three pools off a response. vCPUs are physical cores × the vCPU-to-core ratio. */
export function clusterPools(output: QuickSizerOutput, vcpuToCoreRatio: number): ClusterPools {
  const demand = output.workloadDemand;
  const growth = 1 + demand.growthPercent / 100;
  return {
    availableVcpus: output.availableCores * vcpuToCoreRatio,
    availableMemoryGb: output.availableMemoryGb,
    availableStorageGb: availableForWorkloadsGb(output.capacityWaterfall),
    cpuDemandFactor: (demand.avgCpuUtilizationPercent / 100) * growth,
    memoryDemandFactor: (demand.avgMemoryUtilizationPercent / 100) * growth,
    storageDemandFactor: growth,
  };
}

/** Pools the headline tiles would show as 0, rounded the way the tiles print them. */
export function emptyWorkloadPools(output: QuickSizerOutput): WorkloadPool[] {
  const shownAsZero: Record<WorkloadPool, boolean> = {
    STORAGE: Math.round((availableForWorkloadsGb(output.capacityWaterfall) / GB_PER_TB) * 100) <= 0,
    MEMORY: Math.round(output.availableMemoryGb) <= 0,
    CPU: Math.round(output.availableCores) <= 0,
  };
  return (Object.keys(shownAsZero) as WorkloadPool[]).filter(pool => shownAsZero[pool]);
}

/** How much of each pool this size's max VM count uses: the engine's own bound, read back as a share. */
export function headroomRows(
  row: QuickSizerVmSizingRow,
  pools: ClusterPools | null,
): HeadroomRow[] {
  if (!pools || row.maxVms <= 0) {
    return [];
  }
  return [
    line('CPU', row.maxVms * row.vcpu * pools.cpuDemandFactor, pools.availableVcpus, 'vCPU', row),
    line('Memory', row.maxVms * row.ramGb * pools.memoryDemandFactor, pools.availableMemoryGb, 'GB', row),
    line('Storage', (row.maxVms * row.diskGb * pools.storageDemandFactor) / GB_PER_TB,
      pools.availableStorageGb / GB_PER_TB, 'TB', row),
  ];
}

const DIGITS_BY_UNIT: Record<string, string> = { vCPU: '1.0-0', GB: '1.0-1', TB: '1.2-2' };

function line(
  resource: HeadroomRow['resource'],
  used: number,
  pool: number,
  unit: string,
  row: QuickSizerVmSizingRow,
): HeadroomRow {
  // A zero pool reads as 0%, not Infinity.
  const percent = pool > 0 ? (used / pool) * 100 : 0;
  return {
    resource,
    used,
    pool,
    percent,
    barPercent: Math.min(100, Math.max(0, percent)),
    unit,
    digits: DIGITS_BY_UNIT[unit] ?? '1.0-0',
    // Whole VMs only, so big sizes can stop well short of 100%.
    limiting: row.limitingResource === resource,
  };
}
